import type { IDBPDatabase } from 'idb'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import {
  DATABASE_NAME,
  databaseExists,
  isIndexedDbAvailable,
  openDatabase,
  openExistingDatabase,
  type SolaDatabase,
} from './db'
import { type StorageError, storageError, toStorageError } from './errors'
import type { Migration } from './migrations'
import { readAllStores } from './readAll'
import { type RecoverySnapshotRef, writeSnapshot } from './recoveryStore'

/**
 * Boot outcome.
 *
 * `recovery` carries a live connection at the PRIOR schema version, so recovery
 * mode can still read and export the user's data.
 */
export type BootResult =
  | { readonly status: 'ready'; readonly db: SolaDatabase; readonly snapshot?: RecoverySnapshotRef }
  | {
      readonly status: 'recovery'
      readonly db: IDBPDatabase | undefined
      readonly error: StorageError
      readonly storedVersion: number | undefined
      readonly expectedVersion: number
      readonly snapshot?: RecoverySnapshotRef
    }
  | { readonly status: 'unavailable'; readonly error: StorageError }

export interface BootOptions {
  readonly name?: string
  readonly targetVersion?: number
  readonly migrations?: readonly Migration[]
  readonly onBlocked?: () => void
  /** Skip the preflight snapshot. Tests only — never in production. */
  readonly skipPreflightSnapshot?: boolean
}

/**
 * The D-028 boot sequence.
 *
 *   1. confirm IndexedDB is usable
 *   2. probe the stored version WITHOUT triggering an upgrade
 *   3. if a migration is needed, take a durable recovery snapshot in a
 *      PREFLIGHT — ordinary async work, no transaction open — then close
 *   4. open at the target version so `upgrade` runs in one versionchange
 *      transaction containing only synchronous IndexedDB work
 *   5. on abort, reopen at the prior version and enter recovery mode
 *
 * The snapshot is deliberately taken before the connection is closed and before
 * the higher version is opened. It cannot be taken inside `upgrade`: a
 * versionchange transaction cannot contain OPFS writes, ZIP assembly, or
 * `crypto.subtle` calls without committing early.
 */
export async function bootDatabase(options: BootOptions = {}): Promise<BootResult> {
  const name = options.name ?? DATABASE_NAME
  const targetVersion = options.targetVersion ?? DB_SCHEMA_VERSION

  if (!isIndexedDbAvailable()) {
    return {
      status: 'unavailable',
      error: storageError(
        'unavailable',
        'Project Sola needs local browser storage, which is not available in this context. Private browsing or disabled site data can cause this.',
      ),
    }
  }

  let probeOutcome: ProbeOutcome
  try {
    probeOutcome = await probeStoredVersion(name, targetVersion, options)
  } catch (cause) {
    return { status: 'unavailable', error: toStorageError(cause) }
  }

  if (probeOutcome.kind === 'refuse') return probeOutcome.result

  const { storedVersion, snapshot } = probeOutcome

  try {
    const db = await openDatabase({
      name,
      version: targetVersion,
      ...(options.migrations === undefined ? {} : { migrations: options.migrations }),
      ...(options.onBlocked === undefined ? {} : { onBlocked: options.onBlocked }),
      onBlocking: () => {
        // Another context wants to upgrade; release this connection so it is
        // not blocked indefinitely.
        db?.close()
      },
    })
    return snapshot === undefined ? { status: 'ready', db } : { status: 'ready', db, snapshot }
  } catch (cause) {
    // The upgrade aborted. IndexedDB has rolled the whole versionchange
    // transaction back, so the stored version is unchanged and the prior schema
    // is still openable.
    const error = toStorageError(cause)
    const priorConnection = await reopenPriorVersion(name)

    return {
      status: 'recovery',
      db: priorConnection,
      storedVersion,
      expectedVersion: targetVersion,
      error: storageError(
        'migrationFailed',
        `Updating the local database from version ${storedVersion ?? 'unknown'} to ${targetVersion} did not complete. Your existing data was left unchanged.`,
        error.cause ?? cause,
      ),
      ...(snapshot === undefined ? {} : { snapshot }),
    }
  }
}

type ProbeOutcome =
  | {
      readonly kind: 'proceed'
      readonly storedVersion: number | undefined
      readonly snapshot: RecoverySnapshotRef | undefined
    }
  | { readonly kind: 'refuse'; readonly result: BootResult }

/**
 * Step 2–3: discover the stored version and, if a migration is needed, take the
 * preflight snapshot — all before the higher version is ever opened.
 */
async function probeStoredVersion(
  name: string,
  targetVersion: number,
  options: BootOptions,
): Promise<ProbeOutcome> {
  const exists = await databaseExists(name)

  // A fresh install needs no probe. Probing would CREATE an empty database at
  // version 1, which would then look like an already-completed migration and
  // silently skip store creation.
  if (exists === false) {
    return { kind: 'proceed', storedVersion: undefined, snapshot: undefined }
  }

  const probe = await openExistingDatabase(name)

  // If `databases()` was unavailable, the probe itself may have created the
  // database. An empty store list means it is really fresh.
  const looksFresh = exists === 'unknown' && probe.objectStoreNames.length === 0
  const storedVersion = looksFresh ? undefined : probe.version

  if (storedVersion !== undefined && storedVersion > targetVersion) {
    // Data written by a newer build. Opening at a lower version would throw
    // VersionError; refuse and explain instead. The probe stays open so
    // recovery mode can still read the data.
    return {
      kind: 'refuse',
      result: {
        status: 'recovery',
        db: probe,
        storedVersion,
        expectedVersion: targetVersion,
        error: storageError(
          'versionTooNew',
          `This data was written by a newer version of Project Sola (database version ${storedVersion}, this build understands ${targetVersion}). Update the app rather than continuing here.`,
        ),
      },
    }
  }

  const needsMigration = storedVersion !== undefined && storedVersion < targetVersion
  const snapshot =
    needsMigration && options.skipPreflightSnapshot !== true
      ? await runPreflight(probe, storedVersion, targetVersion)
      : undefined

  probe.close()
  return { kind: 'proceed', storedVersion, snapshot }
}

/**
 * Preflight: read every store and write a durable recovery snapshot.
 *
 * Runs with NO transaction open across the awaits. `readAllStores` uses one
 * short read-only transaction per store and assembles between them, so an OPFS
 * write can never sit inside a live transaction.
 *
 * Structured records only — blob payloads are immutable, so a migration cannot
 * damage them and copying them would double storage for nothing (D-029).
 */
async function runPreflight(
  probe: IDBPDatabase,
  fromVersion: number,
  toVersion: number,
): Promise<RecoverySnapshotRef | undefined> {
  try {
    const records = await readAllStores(probe, { includeBlobs: false })
    return await writeSnapshot({
      purpose: 'pre-migration',
      fromVersion,
      toVersion,
      records,
    })
  } catch {
    // A snapshot is defence in depth against a migration that succeeds but is
    // logically wrong. IndexedDB's own atomic rollback still protects against a
    // migration that throws, so failing to snapshot does not justify blocking
    // the user from their data.
    return undefined
  }
}

async function reopenPriorVersion(name: string): Promise<IDBPDatabase | undefined> {
  try {
    return await openExistingDatabase(name)
  } catch {
    return undefined
  }
}
